import { MedplumClient } from "@medplum/core";

import { rebuildCombinedProcedureCodes } from "@/settings/components/FeeTable/services/combined-procedure-codes";
import { NonCustomCategories } from "@/shared/constants/fee-schedule-categories";

/** The practice's CPT search ValueSet and the codes the practice has turned on. */
export type ProceduresPickerValueSet = {
  valueSetUrl: string;
  /** Codes in the combined ValueSet: the practice's turned-on fee schedule and custom codes. */
  activeCodes: ReadonlySet<string>;
  /**
   * Categories whose codes could not be read into the combined ValueSet, so `activeCodes` can't
   * say which of their codes the practice turned on.
   */
  uncheckedCategories: ReadonlySet<NonCustomCategories>;
};

export type EnsureProceduresPickerValueSetResult =
  | { ok: true; data: ProceduresPickerValueSet }
  /** `rebuild-failed`: the practice's combined procedure codes ValueSet could not be written. */
  | { ok: false; reason: "rebuild-failed"; details?: unknown };

/**
 * Builds the practice's combined procedure codes ValueSet so the Procedures picker can search it.
 *
 * The picker expands the combined ValueSet by URL, and a practice has none until something builds
 * it, so the picker builds it on open the same way the CPT drawers do.
 *
 * @param medplum The signed-in Medplum client; the rebuild runs with that user's access policy.
 * @param practiceId The practice Organization id the combined ValueSet belongs to.
 * @returns The combined ValueSet URL, its codes and the categories it could not include on
 *   success, or `rebuild-failed` with the underlying error.
 */
export const ensureProceduresPickerValueSet = async (
  medplum: MedplumClient,
  practiceId: string,
): Promise<EnsureProceduresPickerValueSetResult> => {
  try {
    const { valueSet, unexpandedCategories } = await rebuildCombinedProcedureCodes(
      medplum,
      practiceId,
    );
    if (!valueSet.url) return { ok: false, reason: "rebuild-failed" };

    const activeCodes = new Set(
      (valueSet.expansion?.contains ?? []).flatMap((coding) =>
        coding.code && !coding.inactive ? [coding.code] : [],
      ),
    );
    return {
      ok: true,
      data: {
        valueSetUrl: valueSet.url,
        activeCodes,
        uncheckedCategories: new Set(unexpandedCategories),
      },
    };
  } catch (error) {
    return { ok: false, reason: "rebuild-failed", details: error };
  }
};
